/**
 * 단골 카드 (DEV_SPEC 4.1 마무리, 시안 06 S0b-8) → 2막 장면 전환(S0·T2) 또는 이모 1:1(O1).
 *
 * 전면 사진(두 찻잔) 위 **아래 정렬** 카드: 찻잔 마크 → 「인생상회 단골」 → 호칭 → 첫 방문일 · 늘 마시는 차(없으면 날짜만)
 * → 환영 인사(사주 유무와 관계없이 같은 문구, 운세 · 니체 · 알림 얘기 없음) → 「환영해요」.
 * 누르면 카드가 아래로 내려가며(240ms) 다음 장면으로.
 * **여기서 알림을 묻지 않습니다**(시안 05 결정 19). 매일 콘텐츠 알림은 기본값으로 두고, 기기 알림 권한은 다음 날
 * 처음 들어와 아침 카드를 본 뒤 카드 아래 이모 한 줄로 한 번만 묻습니다(PushAskLine).
 * 카드 뒤: 별채로 건너가는 경로면 「2막 · 별채에 다섯 사람이 둘러앉아 있어요」 → 별채대화.
 * 「오늘은 이모랑만요」면 이모 1:1 로 가서 「인생상회 단골이 됐어요」를 보여 주고 이모가 대화를 잇습니다(O1).
 */
import { useState } from 'react';
import { prefersReducedMotion } from '../welcome/SceneMotion.jsx';
import { useNavigate } from 'react-router-dom';
import { BrandMark } from '../../components/Brand.jsx';
import { Button } from '../../components/Button.jsx';
import { errorMessageOf } from '../../api/client.js';
import * as counterApi from '../../api/counter.js';
import { useSession } from '../../stores/session.js';
import { AnnexTransition, sceneUrl } from '../welcome/SceneCards.jsx';

const CARD_LEAVE_MS = 240;

export function RegularCard({ regular }) {
    const navigate = useNavigate();
    const setUser = useSession((state) => state.setUser);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [joinedUser, setJoinedUser] = useState(null);
    const [leaving, setLeaving] = useState(false);

    async function enter() {
        setBusy(true);
        setError(null);
        try {
            // 매일 콘텐츠 알림은 기본값 그대로(권한은 다음 날 묻습니다)
            const { user } = await counterApi.finishCounter({ notifications: true });
            // 카드가 아래로 내려간 뒤 다음 장면으로
            if (!prefersReducedMotion()) {
                setLeaving(true);
                await new Promise((resolve) => setTimeout(resolve, CARD_LEAVE_MS));
            }
            if (regular.afterCard === 'imo') {
                setUser(user);
                navigate('/chat/imo', { replace: true, state: { justJoined: true } });
                return;
            }
            setJoinedUser(user);
        } catch (requestError) {
            setError(errorMessageOf(requestError, '단골 등록을 못 했어요. 잠시 후 다시 시도해 주세요.'));
            setLeaving(false);
            setBusy(false);
        }
    }

    if (joinedUser) {
        return (
            <AnnexTransition
                onEnter={() => {
                    setUser(joinedUser);
                    navigate('/', { replace: true, state: { fromAnnex: true } });
                }}
            />
        );
    }

    return (
        <div className="fixed inset-0 z-40 mx-auto flex max-w-app flex-col justify-end overflow-hidden motion-safe:animate-dim-in">
            <img
                src={sceneUrl('two-cups')}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 size-full object-cover"
                style={{ objectPosition: 'center top' }}
            />
            <span
                aria-hidden="true"
                className="absolute inset-0"
                style={{
                    background: 'linear-gradient(rgba(38,35,31,0.15), rgba(38,35,31,0.4) 50%, rgba(38,35,31,0.6))',
                }}
            />
            <div className="relative p-6">
                <section
                    role="dialog"
                    aria-modal="true"
                    aria-label="단골 카드"
                    className={`flex w-full flex-col items-center gap-3 rounded-[20px] bg-surface px-6 pt-7 pb-5 text-center transition-[transform,opacity] duration-[240ms] ease-in ${
                        leaving ? 'translate-y-[120%] opacity-0' : ''
                    }`}
                >
                    <BrandMark size={40} />
                    <span className="text-section text-label-muted">인생상회 단골</span>
                    <span className="font-serif text-[28px] leading-[36px] font-bold text-brand">
                        {regular.displayName}
                    </span>
                    <span className="text-[14px] text-text-secondary">
                        첫 방문 {regular.firstVisit}
                        {regular.tea ? ` · 늘 마시는 차 ${regular.tea}` : ''}
                    </span>
                    <p className="m-0 mt-1 mb-2 font-serif text-[16px] leading-[26px]">{regular.welcome}</p>
                    {error ? (
                        <p role="alert" className="m-0 text-caption text-error">
                            {error}
                        </p>
                    ) : null}
                    <Button fullWidth busy={busy} onClick={enter}>
                        환영해요
                    </Button>
                </section>
            </div>
        </div>
    );
}
